import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppDataContext } from '../../context/AppDataContext';
import { useProducts } from '../../context/ProductsContext';
import { useSync } from '../../context/SyncContext';
import { useTranslation } from '../../i18n/index.jsx';
import Icon from '../common/Icon';
import { APP_VERSION } from '../../config/constants';
import LoadingSpinner from '../common/LoadingSpinner';
import { useCart } from '../../context/CartContext';
import AIFloatingButton from '../common/AIFloatingButton';
import CartFloatingButton from '../common/CartFloatingButton';
import StaffHomeTab from './StaffHomeTab';
import { commitOrder } from '../../helpers/orderHelpers';
import { clockIn as attendanceClockIn, clockOut as attendanceClockOut, getTodayRecord, getAttendancePath } from '../../helpers/attendanceHelpers';
import { ref as dbRef, onValue, off } from 'firebase/database';
import { database } from '../../firebase/config';

// Lazy-loaded route components
const ProductsPage = lazy(() => import('../admin/ProductsPage'));
const IntakePage = lazy(() => import('../intake/IntakePage'));
const QuizPage = lazy(() => import('./QuizPage'));
const StaffSalesTab = lazy(() => import('./StaffSalesTab'));
const StaffProfileTab = lazy(() => import('./StaffProfileTab'));

// Lazy-loaded modals (heavy — html2canvas, jsPDF, qrcode.react)
const POSModal = lazy(() => import('../modals/POSModal'));
const StockCheckModal = lazy(() => import('../modals/StockCheckModal'));
const ClockOutModal = lazy(() => import('../modals/ClockOutModal'));
const PettyCashModal = lazy(() => import('../modals/PettyCashModal'));
const ReceivingCheckModal = lazy(() => import('../modals/ReceivingCheckModal'));

// Route → bottom nav tab mapping for staff
const ROUTE_TO_TAB = {
  '/': 'home',
  '/search': 'search',
  '/inventory': 'search',
  '/sales': 'sales',
  '/quiz': 'quiz',
  '/profile': 'profile',
  '/intake': null,
};

function getTabFromPath(pathname) {
  if (ROUTE_TO_TAB[pathname] !== undefined) return ROUTE_TO_TAB[pathname];
  return 'home';
}

export default function StaffApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, handleLogout } = useAuth();
  const {
    logs, setLogs,
    orders, setOrders,
    customers, setCustomers,
    memos, setMemos,
    tasks: globalTasks, setTasks: setGlobalTasks,
    users,
    pettyCash, setPettyCash,
    quizResults, setQuizResults,
    shipments, setShipments,
    purchases, setPurchases,
    stockCheckRotation, setStockCheckRotation,
    stockAdjustmentLog, setStockAdjustmentLog,
  } = useAppDataContext();
  const { bjProducts, setBjProducts, marucoProducts, setMarucoProducts } = useProducts();
  const { cartCount } = useCart();
  const { syncStatus } = useSync();
  const { t } = useTranslation();

  // --- Modal state ---
  const [clockedIn, setClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [showClockOut, setShowClockOut] = useState(false);
  const [intakePrefillSku, setIntakePrefillSku] = useState(null);
  const [showStockCheck, setShowStockCheck] = useState(false);
  const [stockCheckCompleted, setStockCheckCompleted] = useState(false);
  const [showPOS, setShowPOS] = useState(false);
  const [showPettyCash, setShowPettyCash] = useState(false);
  const [showReceiving, setShowReceiving] = useState(false);
  const [receivingShipment, setReceivingShipment] = useState(null);

  const activeTab = getTabFromPath(location.pathname);

  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const monthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  const allProducts = useMemo(() => [...bjProducts, ...marucoProducts], [bjProducts, marucoProducts]);

  // Check today's attendance from Firebase
  useEffect(() => {
    getTodayRecord(user.id).then(record => {
      if (record && !record.clock_out) {
        setClockedIn(true);
        setClockInTime(record.clock_in);
      } else if (record && record.clock_out) {
        setClockedIn(false);
        setClockInTime(null);
      }
    });

    const month = todayStr.slice(0, 7);
    const attendRef = dbRef(database, getAttendancePath(month, user.id) + '/' + todayStr);
    const cb = (snap) => {
      const record = snap.val();
      if (record) {
        if (!record.clock_out) {
          setClockedIn(true);
          setClockInTime(record.clock_in);
        } else {
          setClockedIn(false);
          setClockInTime(null);
        }
      }
    };
    onValue(attendRef, cb);

    return () => off(attendRef, 'value', cb);
  }, [user.id, todayStr]);

  const workingTime = useMemo(() => {
    if (!clockedIn || !clockInTime) return null;
    const start = new Date(`${todayStr}T${clockInTime}`);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000 / 60);
    return { hours: Math.floor(diff / 60), minutes: diff % 60 };
  }, [clockedIn, clockInTime, todayStr]);

  const todaySalesOrders = useMemo(() => {
    return (orders || []).filter(o => o.date === todayStr && o.status === 'completed');
  }, [orders, todayStr]);

  const tasks = useMemo(() => {
    const todayQuizCount = quizResults.filter(r => r.date === todayStr && r.staffId === user.id).length;
    const list = [
      { id: 'clockin', name: t('staff.task.clockIn'), points: 50, done: clockedIn },
      { id: 'check', name: t('staff.task.stockCheck'), points: 100, done: stockCheckCompleted, clickable: clockedIn && !stockCheckCompleted, locked: !clockedIn },
      { id: 'quiz',
        name: todayQuizCount > 0 && todayQuizCount < 3 ? `${t('staff.task.quiz')} (${todayQuizCount}/3)` : t('staff.task.quiz'),
        points: 150,
        done: todayQuizCount >= 3,
        clickable: todayQuizCount < 3,
      },
      { id: 'sales', name: t('staff.task.salesEntry'), points: 50, done: todaySalesOrders.length > 0 },
      { id: 'close', name: t('staff.task.closeCheck'), points: 30, done: false },
    ];
    return {
      total: list.length,
      completed: list.filter(tk => tk.done).length,
      list
    };
  }, [clockedIn, stockCheckCompleted, todaySalesOrders.length, quizResults, todayStr, user.id, t]);

  const handleClockIn = async () => {
    try {
      const result = await attendanceClockIn({ userId: user.id, userName: user.name });
      setClockedIn(true);
      setClockInTime(result.clockIn);
      setStockCheckCompleted(false);

      if (stockCheckRotation?.last_date !== todayStr) {
        const currentIdx = stockCheckRotation?.rotation_index || 0;
        setStockCheckRotation({
          rotation_index: (currentIdx + 1) % 21,
          last_date: todayStr,
        });
      }
    } catch (err) {
      console.error('Clock in failed:', err);
    }
  };

  const handleOpenIntake = () => {
    navigate('/intake');
  };

  // ── Full-page Intake View ──
  if (location.pathname === '/intake') {
    return (
      <div className="fixed inset-0 z-50">
        <Suspense fallback={<LoadingSpinner />}>
          <IntakePage
            onClose={() => { setIntakePrefillSku(null); navigate(-1); }}
            prefillSku={intakePrefillSku}
          />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-t-bg flex flex-col">
      {/* Header */}
      <header className="bg-t-card/80 backdrop-blur-sm border-b border-t-border px-4 py-2 safe-area-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-xl mr-2">{'\uD83C\uDFEA'}</span>
            <div>
              <div className="text-t-primary font-bold text-sm flex items-center gap-2">
                {t('app.companyName')}
                <span className="text-t-secondary text-xs font-normal">{APP_VERSION}</span>
                <span className={`w-2 h-2 rounded-full ${syncStatus === 'online' ? 'bg-green-500' : syncStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'}`} title={syncStatus === 'online' ? t('sync.online') : syncStatus === 'connecting' ? t('sync.connecting') : t('sync.offline')}></span>
              </div>
              <div className="text-t-secondary text-xs">{user.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {clockedIn && workingTime && (
              <div className="text-right">
                <div className="text-t-accent text-sm font-medium">{workingTime.hours}h {workingTime.minutes}m</div>
                <div className="text-t-secondary text-xs">{t('staff.working')}</div>
              </div>
            )}
            <button onClick={handleLogout} className="text-t-secondary hover:text-t-primary"><Icon name="LogOut" size={20} /></button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-4 pb-24">
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={
            <StaffHomeTab
              clockedIn={clockedIn}
              clockInTime={clockInTime}
              today={today}
              todayStr={todayStr}
              tasks={tasks}
              todaySalesOrders={todaySalesOrders}
              onClockIn={handleClockIn}
              onClockOut={() => setShowClockOut(true)}
              onShowStockCheck={() => setShowStockCheck(true)}
              onShowIntake={handleOpenIntake}
              onShowPOS={() => setShowPOS(true)}
              onShowPettyCash={() => setShowPettyCash(true)}
              onShowReceiving={(shipment) => { setReceivingShipment(shipment); setShowReceiving(true); }}
              onNavigateQuiz={() => navigate('/quiz')}
            />
          } />
          <Route path="/search" element={
            <ProductsPage
              onAddStock={(product) => { setIntakePrefillSku(product?.sku || null); navigate('/intake'); }}
            />
          } />
          <Route path="/inventory" element={<Navigate to="/search" replace />} />
          <Route path="/sales" element={
            <StaffSalesTab onShowPOS={() => setShowPOS(true)} />
          } />
          <Route path="/quiz" element={
            <QuizPage quizResults={quizResults} setQuizResults={setQuizResults} user={user} />
          } />
          <Route path="/profile" element={
            <StaffProfileTab tasks={tasks} monthStr={monthStr} />
          } />
          {/* Route guards: staff cannot access admin-only routes */}
          <Route path="/suppliers" element={<Navigate to="/" replace />} />
          <Route path="/settings" element={<Navigate to="/" replace />} />
          <Route path="/more" element={<Navigate to="/" replace />} />
          {/* Catch-all: redirect unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-t-nav/80 backdrop-blur-xl border-t border-t-border safe-area-bottom z-40">
        <div className="flex">
          {[
            { id: 'home', path: '/', icon: 'Home', label: t('staff.navHome') },
            { id: 'sales', path: '/sales', icon: 'Receipt', label: t('staff.navSales'), badge: cartCount },
            { id: 'search', path: '/search', icon: 'Search', label: t('staff.navSearch') },
            { id: 'quiz', path: '/quiz', icon: 'Gamepad2', label: t('staff.navQuiz') },
            { id: 'profile', path: '/profile', icon: 'User', label: t('staff.navProfile') },
          ].map(tab => (
            <button key={tab.id} onClick={() => navigate(tab.path)} className={`flex-1 py-2 pt-3 flex flex-col items-center relative transition ${activeTab === tab.id ? 'text-t-accent' : 'text-t-nav-inactive'}`}>
              {activeTab === tab.id && (
                <span className="absolute top-0.5 w-1 h-1 rounded-full bg-t-accent" />
              )}
              <Icon name={tab.icon} size={22} />
              <span className="text-[10px] mt-1 font-medium">{tab.label}</span>
              {tab.badge > 0 && (
                <span className="absolute top-1 left-1/2 ml-2 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      {/* Stock Check Modal */}
      {showStockCheck && (
        <Suspense fallback={<LoadingSpinner />}>
        <StockCheckModal
          products={allProducts}
          user={user}
          onComplete={() => setStockCheckCompleted(true)}
          onClose={() => setShowStockCheck(false)}
          onUpdateProducts={(productId, brand, updates) => {
            if (brand === 'BJ') {
              setBjProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updates } : p));
            } else {
              setMarucoProducts(prev => prev.map(p => p.id === productId ? { ...p, ...updates } : p));
            }
          }}
          stockCheckRotation={stockCheckRotation}
          setStockCheckRotation={setStockCheckRotation}
          stockAdjustmentLog={stockAdjustmentLog}
          setStockAdjustmentLog={setStockAdjustmentLog}
        />
        </Suspense>
      )}

      {/* POS Sales Modal */}
      {showPOS && (
        <Suspense fallback={<LoadingSpinner />}>
        <POSModal
          bjProducts={bjProducts}
          marucoProducts={marucoProducts}
          user={user}
          onClose={() => setShowPOS(false)}
          onComplete={async (saleData) => {
            return commitOrder({
              saleData,
              user,
              updateStock: (items) => {
                items.forEach(item => {
                  if (item.brand === 'BJ') {
                    setBjProducts(prev => prev.map(p => {
                      if (p.id !== item.id) return p;
                      const updates = { stock: (p.stock || 0) - item.qty };
                      if (item.savePrice) updates.price = item.price;
                      return { ...p, ...updates };
                    }));
                  } else {
                    setMarucoProducts(prev => prev.map(p => {
                      if (p.id !== item.id) return p;
                      const updates = { stock: (p.stock || 0) - item.qty };
                      if (item.savePrice) updates.price = item.price;
                      return { ...p, ...updates };
                    }));
                  }
                });
              },
            });
          }}
        />
        </Suspense>
      )}

      {/* Petty Cash Modal */}
      {showPettyCash && (
        <Suspense fallback={<LoadingSpinner />}>
        <PettyCashModal
          pettyCash={pettyCash}
          setPettyCash={setPettyCash}
          user={user}
          users={users}
          onClose={() => setShowPettyCash(false)}
        />
        </Suspense>
      )}

      {/* Receiving Check Modal */}
      {showReceiving && receivingShipment && (
        <Suspense fallback={<LoadingSpinner />}>
        <ReceivingCheckModal
          shipment={receivingShipment}
          products={allProducts}
          user={user}
          onComplete={(result) => {
            const today = new Date().toISOString().split('T')[0];

            setShipments(prev => (prev || []).map(s => s.id === result.shipmentId ? {
              ...s,
              status: result.discrepancies.length > 0 ? 'partial' : 'received',
              receivedAt: result.receivedAt,
              receivedBy: result.receivedBy,
              items: result.items,
              discrepancies: result.discrepancies,
            } : s));

            const newPurchases = result.items
              .filter(item => item.actualQty > 0)
              .map((item, idx) => ({
                id: Date.now() + idx,
                date: today,
                supplier: receivingShipment.supplier,
                productId: item.productId || '',
                productName: item.name,
                brand: (item.location?.startsWith('L') || item.location?.startsWith('R')) ? 'MARUCO' : 'BJ',
                qty: item.actualQty,
                unitPrice: item.unitPrice,
                total: item.actualQty * item.unitPrice,
                shipmentId: result.shipmentId,
                createdAt: new Date().toISOString(),
              }));
            setPurchases(prev => [...newPurchases, ...(prev || [])]);

            result.items.filter(item => item.actualQty > 0 && item.productId).forEach(item => {
              if (item.productId.startsWith('BJ|')) {
                setBjProducts(prev => prev.map(p => p.id === item.productId ? { ...p, stock: (p.stock || 0) + item.actualQty } : p));
              } else {
                setMarucoProducts(prev => prev.map(p => p.id === item.productId ? { ...p, stock: (p.stock || 0) + item.actualQty } : p));
              }
            });

            const newLogs = result.items
              .filter(item => item.actualQty > 0)
              .map((item, idx) => ({
                id: `${Date.now()}-recv-${idx}`,
                date: today,
                slipNo: `RCV-${today.slice(2).replace(/-/g, '')}-${String(idx + 1).padStart(3, '0')}`,
                productId: item.productId || '',
                productName: item.name,
                type: t('ai.stockIn'),
                qty: item.actualQty,
                cost: item.unitPrice || 0,
                price: 0,
                total: (item.unitPrice || 0) * item.actualQty,
                partner: receivingShipment.supplier,
                staff: user.name,
                shipmentId: result.shipmentId,
              }));
            setLogs(prev => [...newLogs, ...prev]);

            if (receivingShipment.taskId) {
              setGlobalTasks(prev => (prev || []).map(tk =>
                tk.id === receivingShipment.taskId ? { ...tk, done: true, completedAt: new Date().toISOString() } : tk
              ));
            }

            if (result.discrepancies.length > 0) {
              const discMemos = result.discrepancies.map((d, idx) => ({
                id: Date.now() + 1000 + idx,
                text: t('receiving.discrepancyNote', {
                  supplier: receivingShipment.supplier,
                  name: d.itemName,
                  expected: d.expected,
                  actual: d.actual,
                  diff: d.diff > 0 ? `+${d.diff}` : `${d.diff}`,
                }),
                author: user.name,
                authorId: user.id,
                createdAt: new Date().toISOString(),
                type: 'alert',
              }));
              setMemos(prev => [...discMemos, ...(prev || [])]);
            }

            setShowReceiving(false);
            setReceivingShipment(null);
            alert(t('receiving.completed', { count: result.items.filter(i => i.actualQty > 0).length }));
          }}
          onClose={() => { setShowReceiving(false); setReceivingShipment(null); }}
        />
        </Suspense>
      )}

      {/* Clock Out Report Modal */}
      {showClockOut && (
        <Suspense fallback={<LoadingSpinner />}>
        <ClockOutModal
          user={user}
          clockInTime={clockInTime}
          todaySales={todaySalesOrders}
          tasks={tasks}
          onConfirm={async () => {
            try {
              await attendanceClockOut({ userId: user.id });
              setClockedIn(false);
              setShowClockOut(false);
            } catch (err) {
              console.error('Clock out failed:', err);
              setShowClockOut(false);
            }
          }}
          onClose={() => setShowClockOut(false)}
        />
        </Suspense>
      )}

      {/* Cart Floating Button */}
      <CartFloatingButton onOpenPOS={() => setShowPOS(true)} />

      {/* AI Floating Button */}
      <AIFloatingButton isAdmin={false} onOpenIntake={handleOpenIntake} />
    </div>
  );
}
